import { Check, Zap, KeyRound } from "lucide-react";

const tiers = [
  {
    id: "tier1",
    name: "Tier 1",
    tagline: "Website Only",
    price: 150,
    accent: "#00f0ff",
    featured: false,
    support: "Basic support — standard queue for urgent requests",
    features: [
      "Professional website design & development",
      "Look credible, attract more clients",
      "Mobile-first, fast loading",
      "Basic ongoing support",
      "Standard priority on urgent requests",
    ],
  },
  {
    id: "tier2",
    name: "Tier 2",
    tagline: "Website + App",
    price: 300,
    accent: "#00f0ff",
    featured: false,
    support: "Elevated support — above Tier 1, below Tier 3",
    features: [
      "Everything in Tier 1",
      "Full mobile app (iOS & Android)",
      "Automate processes & improve workflows",
      "Online ordering / booking / intake flows",
      "Higher support priority than Tier 1",
    ],
  },
  {
    id: "tier3",
    name: "Tier 3",
    tagline: "Complex Websites & Apps",
    price: 500,
    accent: "#ff003c",
    featured: true,
    support: "24/7 support — always first in line, highest priority",
    features: [
      "Everything in Tier 2",
      "Advanced & custom builds",
      "Deep automation & integrations",
      "24/7 support for updates and changes",
      "Highest priority — always served first",
    ],
  },
];

export default function Pricing({ onSelectPlan }) {
  return (
    <section id="plans" className="relative py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <div className="max-w-2xl">
          <p className="font-mono-tech text-xs uppercase tracking-[0.3em] text-[#00f0ff]">// Subscription plans</p>
          <h2 className="font-display mt-4 text-3xl font-black tracking-tight sm:text-4xl">
            Pick your <span className="text-[#00f0ff] text-glow-cyan">priority level</span>
          </h2>
          <p className="mt-4 text-base text-white/50 md:text-lg">
            Every plan includes design, development, and ongoing management. The difference is
            how fast we move when you need us.
          </p>
        </div>

        <div className="mt-16 grid gap-6 lg:grid-cols-3">
          {tiers.map((t) => (
            <article
              key={t.id}
              data-testid={`pricing-card-${t.id}`}
              className={`relative flex flex-col border bg-[#0a0a0a] p-8 transition-shadow duration-300 lg:p-10 ${
                t.featured ? "border-[#ff003c] glow-magenta" : "border-white/10 hover:glow-cyan"
              }`}
            >
              {t.featured && (
                <span className="font-mono-tech absolute -top-3 left-8 flex items-center gap-1.5 bg-[#ff003c] px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-white">
                  <Zap size={11} /> Highest Priority
                </span>
              )}
              <p className="font-mono-tech text-xs uppercase tracking-[0.25em]" style={{ color: t.accent }}>
                {t.name}
              </p>
              <h3 className="font-display mt-2 text-xl font-bold">{t.tagline}</h3>
              <div className="mt-6 flex items-end gap-2">
                <span className="font-display text-5xl font-black">${t.price}</span>
                <span className="font-mono-tech pb-1.5 text-xs uppercase tracking-widest text-white/40">/month</span>
              </div>
              <p
                className="font-mono-tech mt-5 border px-3 py-2 text-[11px] uppercase tracking-wider"
                style={{ borderColor: `${t.accent}55`, color: t.accent }}
                data-testid={`pricing-support-${t.id}`}
              >
                {t.support}
              </p>
              <ul className="mt-8 flex-1 space-y-3.5">
                {t.features.map((f) => (
                  <li key={f} className="flex items-start gap-3 text-sm text-white/65">
                    <Check size={16} className="mt-0.5 shrink-0" style={{ color: t.accent }} />
                    {f}
                  </li>
                ))}
              </ul>
              <button
                onClick={() => onSelectPlan(t.id)}
                data-testid={`pricing-select-${t.id}`}
                className={`glitch-hover mt-10 w-full border px-6 py-3.5 font-mono-tech text-xs font-bold uppercase tracking-widest transition-shadow duration-300 ${
                  t.featured
                    ? "border-[#ff003c] bg-[#ff003c]/10 text-[#ff003c] hover:glow-magenta"
                    : "border-[#00f0ff]/60 text-[#00f0ff] hover:glow-cyan"
                }`}
              >
                Choose {t.name}
              </button>
            </article>
          ))}
        </div>

        <article
          data-testid="pricing-card-one-time"
          className="mt-8 grid items-center gap-8 border border-white/15 bg-[#0a0a0a] p-8 lg:grid-cols-[1fr_auto] lg:p-12"
        >
          <div>
            <p className="font-mono-tech flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-white">
              <KeyRound size={14} className="text-[#00f0ff]" /> One-Time Build
            </p>
            <h3 className="font-display mt-3 text-2xl font-bold">Own it outright.</h3>
            <p className="mt-3 max-w-2xl text-sm leading-relaxed text-white/55 md:text-base">
              Prefer a single payment? We build your website and/or app, hand you full ownership,
              and you handle all future support and updates yourself — or bring us back anytime.
              Custom quote based on scope.
            </p>
          </div>
          <button
            onClick={() => onSelectPlan("one-time")}
            data-testid="pricing-select-one-time"
            className="glitch-hover border border-white/40 px-8 py-4 font-mono-tech text-xs font-bold uppercase tracking-widest text-white transition-colors duration-300 hover:border-[#00f0ff] hover:text-[#00f0ff]"
          >
            Request a Quote
          </button>
        </article>
      </div>
    </section>
  );
}
